import { useState, useEffect } from 'react';
import { rest } from '@karpeleslab/klbfw';
import { handleError } from '../components/utils/apiErrorHandler';
import { useDispatch } from 'react-redux';
import { useStore } from 'react-redux';

// Cache to store API responses
const cache = new Map();

/**
 * Generate a cache key based on endpoint and params
 */
const getCacheKey = (endpoint, params = {}) => {
  return `${endpoint}:${JSON.stringify(params)}`;
};

/**
 * Custom hook to replace useRest from react-klbfw-hooks
 * Provides caching to prevent duplicate API calls
 * 
 * @param {string} endpoint - API endpoint
 * @param {object} params - Request parameters
 * @param {string} method - HTTP method (default: 'GET')
 * @param {boolean} skipCache - Skip cache and force a fresh request
 * @param {boolean} silent - Whether to silence error toasts
 * @returns {[data, loading, error, reload]} - Array containing data, loading state, error, and reload function
 */
const useRest = (endpoint, params = {}, method = 'GET', skipCache = false, silent = false) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const store = useStore();
  
  const cacheKey = getCacheKey(endpoint, params);

  // Function to load data (also used for reload)
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check cache first if not skipping cache
      if (!skipCache && cache.has(cacheKey)) {
        setData(cache.get(cacheKey));
        setLoading(false);
        return;
      }
      
      // Make API request
      const response = await rest(endpoint, method, params);
      
      // Store in cache and update state
      cache.set(cacheKey, response);
      setData(response);
    } catch (err) {
      setError(err);
      
      // Log error to console for debugging
      console.error(`Error fetching data from ${endpoint}:`, err);
      
      // Use centralized error handling
      try {
        handleError(store.getState, dispatch, err, silent);
      } catch (handledError) {
        // Error was handled but rethrown - this is expected
        // The component using this hook can handle it further if needed
      }
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount or when dependencies change
  useEffect(() => {
    loadData();
  }, [cacheKey, method, skipCache]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return data, loading state, error, and a reload function
  return [data, loading, error, loadData];
};

// Export a function to clear the cache
export const clearRestCache = (endpoint = null) => {
  if (endpoint) {
    // Clear specific endpoint(s)
    const pattern = new RegExp(`^${endpoint}`);
    [...cache.keys()].forEach(key => {
      if (pattern.test(key)) {
        cache.delete(key);
      }
    });
  } else {
    // Clear entire cache
    cache.clear();
  }
};

export default useRest;